// src/pages/WhereToVote.js
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Map, ArrowUpRight, ArrowRight } from 'lucide-react';
import { fetchWeldVotingLocations, isVspcType, isDropOffType } from '../utils/weldLocations';

const CITY_MAPS = [
  {
    city: 'All Larimer County',
    mapId: '1SIy5Qs82jl-01-aha3btNK_Umtjb7hk',
    description: 'All ballot boxes and vote centers county-wide',
  },
  {
    city: 'Bellvue & Laporte',
    mapId: '1NXTFTGiWlYg5AUdEXRgbG3KSmNh9WMQ',
    description: 'Ballot boxes in Bellvue and Laporte area',
  },
  {
    city: 'Berthoud',
    mapId: '16eS00rwlvGTGQCMmUWRbEPAGAMMPoRg',
    description: 'Ballot boxes in Berthoud',
  },
  {
    city: 'Estes Park',
    mapId: '1s1jZbiytittdxUBTDRtWRFw-7-qwijQ',
    description: 'Ballot boxes and vote centers in Estes Park',
  },
  {
    city: 'Fort Collins',
    mapId: '1hXVcUbkTV4Gw3p60qASSWe8RhiUsn58',
    description: 'Ballot boxes and vote centers in Fort Collins',
  },
  {
    city: 'Loveland',
    mapId: '19b3QtvyczZM9fshGqr6na_fULqUP9JQ',
    description: 'Ballot boxes and vote centers in Loveland',
  },
  {
    city: 'Red Feather Lakes',
    mapId: '16SOtAR5eaIc4If6XkRJVR4mK5HNf0CU',
    description: 'Ballot boxes in Red Feather Lakes',
  },
  {
    city: 'Timnath',
    mapId: '1RoXXiIcm2uSMoQ9AUuVszREfp8UKrEA',
    description: 'Ballot boxes in Timnath',
  },
  {
    city: 'Wellington',
    mapId: '1HzEERVSyXh2CCz_2clkX1tFMRGhiwDs',
    description: 'Ballot boxes in Wellington',
  },
];

const WELD_MARKER_ICONS = {
  vspc: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
  dropoff: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
};

const WELD_FILTERS = [
  { id: 'all', label: 'All locations' },
  { id: 'vspc', label: 'VSPCs only' },
  { id: 'dropoff', label: 'Drop-Off only' },
];

const buildInfoWindowContent = (loc) => {
  const wrap = document.createElement('div');
  wrap.style.fontSize = '13px';
  wrap.style.lineHeight = '1.5';
  wrap.style.maxWidth = '220px';

  const name = document.createElement('div');
  name.style.fontWeight = '600';
  name.style.marginBottom = '2px';
  name.textContent = loc.name;

  const addr = document.createElement('div');
  addr.style.color = '#444';
  addr.textContent = loc.address;

  const type = document.createElement('div');
  type.style.fontWeight = '500';
  type.style.marginTop = '4px';
  type.style.color = isDropOffType(loc.type) ? '#1a56db' : '#ae3030';
  type.textContent = loc.type;

  wrap.appendChild(name);
  wrap.appendChild(addr);
  wrap.appendChild(type);
  return wrap;
};

const WeldVotingMap = ({ county, mapsLoaded }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    fetchWeldVotingLocations()
      .then(data => { if (!cancelled) setLocations(data); })
      .catch(err => {
        console.log('Weld voting locations fetch error:', err);
        if (!cancelled) setError('Could not load voting locations. Please try again later.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Build the map and markers once locations and the Google Maps script are both ready
  useEffect(() => {
    if (!mapsLoaded || !window.google || loading || !mapRef.current || locations.length === 0) return;

    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 9,
        center: { lat: 40.42, lng: -104.7 },
        mapTypeId: 'roadmap',
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    markersRef.current.forEach(({ marker }) => marker.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    locations.forEach(loc => {
      const dropoff = isDropOffType(loc.type);
      const marker = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map: googleMapRef.current,
        title: loc.name,
        icon: dropoff ? WELD_MARKER_ICONS.dropoff : WELD_MARKER_ICONS.vspc,
      });
      marker.addListener('click', () => {
        infoWindowRef.current.setContent(buildInfoWindowContent(loc));
        infoWindowRef.current.open(googleMapRef.current, marker);
      });
      markersRef.current.push({ marker, type: loc.type });
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    });

    googleMapRef.current.fitBounds(bounds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsLoaded, loading, locations]);

  // Show/hide markers when the filter changes, without rebuilding the map
  useEffect(() => {
    markersRef.current.forEach(({ marker, type }) => {
      const visible =
        filter === 'all' ||
        (filter === 'vspc' && isVspcType(type)) ||
        (filter === 'dropoff' && isDropOffType(type));
      marker.setMap(visible ? googleMapRef.current : null);
    });
  }, [filter]);

  const visibleCount = locations.filter(loc =>
    filter === 'all' || (filter === 'vspc' && isVspcType(loc.type)) || (filter === 'dropoff' && isDropOffType(loc.type))
  ).length;

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><MapPin size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">Where to vote</div>
          <div className="sec-desc">2026 General Election — voting locations in {county.name}</div>
        </div>
      </div>

      <div style={{marginBottom:14}}>
        <div style={{fontSize:12, color:'#666', marginBottom:8, fontWeight:500}}>Filter locations:</div>
        <div className="pills">
          {WELD_FILTERS.map(f => (
            <span
              key={f.id}
              className={`pill ${filter === f.id ? 'on' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </span>
          ))}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div
        ref={mapRef}
        style={{
          height: 450,
          borderRadius: 6,
          border: '0.5px solid #ddd',
          marginBottom: 10,
          background: '#f0f4f8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {(loading || !mapsLoaded) && (
          <div style={{textAlign:'center', color:'#999'}}>
            <div style={{fontSize:34}}><Map size={34} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
            <p style={{fontSize:13}}>Loading voting locations…</p>
          </div>
        )}
      </div>

      <div style={{display:'flex', gap:18, flexWrap:'wrap', fontSize:12, color:'#444', marginBottom:8}}>
        <span style={{display:'flex', alignItems:'center', gap:6}}>
          <span style={{display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#ae3030'}}></span>
          VSPC — Voter Service &amp; Polling Center
        </span>
        <span style={{display:'flex', alignItems:'center', gap:6}}>
          <span style={{display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#1a56db'}}></span>
          Drop-Off / 24hr Drop-Off
        </span>
      </div>

      <div style={{fontSize:11, color:'#999', marginBottom:16}}>
        Showing {visibleCount} of {locations.length} locations · <Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Source: {county.name} Clerk &amp; Recorder · <a href={county.whereToVoteUrl} target="_blank" rel="noreferrer" style={{color:'#999'}}>Open county map in a new tab</a>
      </div>

      <div style={{background:'#f0f4f8', borderRadius:6, padding:'14px 16px', fontSize:13, color:'#0c447c', lineHeight:1.8}}>
        <div style={{fontWeight:600, marginBottom:6}}>Important 2026 General Election Dates</div>
        <div>• <strong>Mail ballots sent out</strong> — October 2</div>
        <div>• <strong>Drop boxes open</strong> — October 2</div>
        <div>• <strong>Vote centers open</strong> — October 19</div>
        <div>• <strong>Election Day</strong> — November 3 (Polls open 7am-7pm | All ballots must be received by County Clerk&apos;s Office by 7 pm on Election Day. Postmarks do not count.)</div>
        <div style={{marginTop:10}}>
          <a className="ext-btn red" href="https://www.sos.state.co.us/voter/pages/pub/olvr/verifyNewVoter.xhtml" target="_blank" rel="noreferrer">
            Register or update your registration <ArrowRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} />
          </a>
        </div>
      </div>
    </div>
  );
};

const WhereToVote = ({ county, mapsLoaded }) => {
  const [activeCity, setActiveCity] = useState('All Larimer County');
  const activeMap = CITY_MAPS.find(m => m.city === activeCity);

  if (county?.id === 'weld') {
    return <WeldVotingMap county={county} mapsLoaded={mapsLoaded} />;
  }

  if (county?.whereToVoteType === 'iframe') {
    return (
      <div>
        <div className="sec-head">
          <div className="sec-icon"><MapPin size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
          <div>
            <div className="sec-title">Where to vote</div>
            <div className="sec-desc">2026 General Election — ballot boxes and vote centers in {county.name}</div>
          </div>
        </div>

        <iframe
          src={county.whereToVoteUrl}
          width="100%"
          height="600"
          frameBorder="0"
          title={`${county.name} ballot boxes and vote centers`}
          style={{borderRadius:6, border:'0.5px solid #ddd', marginBottom:10}}
          allowFullScreen
        />

        <div style={{fontSize:11, color:'#999'}}>
          <Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Source: {county.name} Clerk &amp; Recorder · <a href={county.whereToVoteUrl} target="_blank" rel="noreferrer" style={{color:'#999'}}>Open in a new tab</a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><MapPin size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">Where to vote</div>
          <div className="sec-desc">2026 General Election — ballot boxes and vote centers in Larimer County</div>
        </div>
      </div>

      <div style={{marginBottom:14}}>
        <div style={{fontSize:12, color:'#666', marginBottom:8, fontWeight:500}}>Filter by city:</div>
        <div className="pills">
          {CITY_MAPS.map(m => (
            <span
              key={m.city}
              className={`pill ${activeCity === m.city ? 'on' : ''}`}
              onClick={() => setActiveCity(m.city)}
            >
              {m.city}
            </span>
          ))}
        </div>
      </div>

      <div style={{fontSize:13, color:'#666', marginBottom:8}}>
        <MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {activeMap.description}
      </div>

      <iframe
        src={`https://www.google.com/maps/d/u/0/embed?mid=${activeMap.mapId}&ehbc=2E312F&noprof=1`}
        width="100%"
        height="450"
        frameBorder="0"
        title={`${activeCity} ballot boxes and vote centers`}
        style={{borderRadius:6, border:'0.5px solid #ddd', marginBottom:10}}
        allowFullScreen
      />

      <div style={{display:'flex', gap:18, flexWrap:'wrap', fontSize:12, color:'#444', marginBottom:8}}>
        <span style={{display:'flex', alignItems:'center', gap:6}}>
          <span style={{display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#FFC107'}}></span>
          Ballot Drop Box
        </span>
        <span style={{display:'flex', alignItems:'center', gap:6}}>
          <span style={{display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#2196F3'}}></span>
          Vote Center
        </span>
      </div>

      <div style={{fontSize:11, color:'#999', marginBottom:16}}>
        <Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Source: Larimer County Clerk & Recorder · <a href="https://www.larimer.gov/clerk/elections/where-to-vote" target="_blank" rel="noreferrer" style={{color:'#999'}}>View full page at larimer.gov</a>
      </div>

      <div style={{background:'#f0f4f8', borderRadius:6, padding:'14px 16px', fontSize:13, color:'#0c447c', lineHeight:1.8}}>
        <div style={{fontWeight:600, marginBottom:6}}>Important 2026 General Election Dates</div>
        <div>• <strong>Mail ballots sent out</strong> — October 2</div>
        <div>• <strong>Drop boxes open</strong> — October 2</div>
        <div>• <strong>Vote centers open</strong> — October 19</div>
        <div>• <strong>Election Day</strong> — November 3 (Polls open 7am-7pm | All ballots must be received by County Clerk&apos;s Office by 7 pm on Election Day. Postmarks do not count.)</div>
        <div style={{marginTop:10}}>
          <a className="ext-btn red" href="https://www.sos.state.co.us/voter/pages/pub/olvr/verifyNewVoter.xhtml" target="_blank" rel="noreferrer">
            Register or update your registration <ArrowRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} />
          </a>
        </div>
      </div>

      <div style={{marginTop:8}}>
        <a href="https://www.larimer.gov/clerk/elections/where-to-vote" target="_blank" rel="noreferrer" style={{color:'#ae3030', fontWeight:500, fontSize:13}}>
          <ArrowUpRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Full list of locations at Larimer County Clerk
        </a>
      </div>
    </div>
  );
};

export default WhereToVote; 
